import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getProfileById } from '../../services/profile';
import AuthContext from '../Context/AuthProvider';
import ProfileCard, { listToString } from './ProfileCard';
import { FaUser, FaBriefcase, FaSchool, FaHeart, FaPhone, FaUsers, FaLock, FaUserFriends } from 'react-icons/fa';
import PaymentModal from '../common/PaymentModal';
import { toast } from 'react-toastify';

const Section = ({ icon: Icon, title, children }) => (
	<div className="mb-6">
	  <h3 className="text-lg sm:text-xl font-semibold flex items-center gap-2 mb-2">
		<Icon className="text-primary" /> {title}
	  </h3>
	  <div className="pl-2 border-l-4 border-gray-200">{children}</div>
	</div>
  );
  

const ProfilePage = () => {
	const { profile, getToken } = React.useContext(AuthContext);
	const [currentProfile, setCurrentProfile] = React.useState(null);
	const [needPayment, setNeedPayment] = React.useState(false);
	const [showPaymentModal, setShowPaymentModal] = React.useState(false);
	const navigate = useNavigate();
	let { id } = useParams();

	const isCurrentProfile = () => {
		return profile?.id === id;
	};

	React.useEffect(() => {
		let auth = getToken();
		if (auth) {
			if (isCurrentProfile()) {
				navigate('/myprofile');
				return;
			}
			try {
				getProfileById(auth.token, id).then(
					(response) => {
						if (response.data.errors) {
							alert('Issue in fetching profile, please try refreshing');
							throw new Error(response.data.errors);
						} else {
							const { Profile, NeedPayment } = response.data.data.getProfileById;
							setCurrentProfile(Profile);
							setNeedPayment(NeedPayment);
						}
					}
				);
			} catch (error) {
				toast.error('There was an error in fetching the profile', {timeOut: 4000});
				console.error('issue in View profile', error);
			}
		}
	}, []);

	function extractNumber(value) {
		if (!value) return '';
		if(!value.includes('-')) return value;
		const parts = value.split('-');
		return parts[1]?.trim() ? value : '';
	}	

	function HandleEmptyValue(property) {
		if(property === null || property === undefined || property === "")
			return needPayment ? <i>Subscription Required</i> : <i>Not Filled</i>;
		return property;
	}

	function displayProperties(properties) {
		return (
			<div className="grid grid-cols-1 sm:grid-cols-2 gap-0 sm:gap-2">
				{properties.map((property) => {
					return (
						<React.Fragment key={property.label}>
							<div className="font-medium">{property.label}</div>
							<div>{HandleEmptyValue(property.value)}</div>
						</React.Fragment>
					);
				})}
			</div>
		);
	}

	const profileSections = [
		{
			icon: FaSchool,
			title: "Education and Career",
			properties: [
				{ label: 'Educational Qualification', value: listToString(currentProfile?.qualifications) },
				{ label: 'Occupation', value: currentProfile?.occupation },
				{ label: 'Works at', value: currentProfile?.employedIn },
				{ label: 'Annual Income', value: currentProfile?.annualIncome },
				{ label: 'NRI Status', value: currentProfile?.isNri ? 'Yes' : 'No' },
			]
		},
		{
			icon: FaUsers,
			title: "Social Details",
			properties: [
				{ label: 'Marital Status', value: currentProfile?.maritalStatus },
				{ label: 'Mother Tongue', value: currentProfile?.motherTongue },
				{ label: 'Caste', value: currentProfile?.caste },
				{ label: 'Gotra', value: currentProfile?.gotra },
				{ label: 'Nakshatra', value: currentProfile?.nakshatra },
				{ label: 'Kuldevta', value: currentProfile?.kuldevta },
				{ label: 'Rashi', value: currentProfile?.rashi },
				{ label: 'Math', value: currentProfile?.math },
			]
		},
		{
			icon: FaBriefcase,
			title: "Other Details",
			properties: [
				{ label: 'Blood Group', value: currentProfile?.bloodGroup },
				{ label: 'Food Habits', value: currentProfile?.foodHabits },
				{ label: 'Smoking Habits', value: currentProfile?.smokingHabits },
				{ label: 'Drinking Habits', value: currentProfile?.drinkingHabits },
				{ label: 'State', value: currentProfile?.state },
				{ label: 'Country', value: currentProfile?.country },
			]
		},
		{
			icon: FaUserFriends,
			title: "Family Details",
			properties: [
				{ label: 'Father\'s Name', value: currentProfile?.fatherName },
				{ label: 'Father\'s Contact', value: extractNumber(currentProfile?.fatherNumber) },
				{ label: 'Mother\'s Name', value: currentProfile?.motherName },
				{ label: 'Mother\'s Contact', value: extractNumber(currentProfile?.motherNumber) },
			]
		},
		{
			icon: FaPhone,
			title: "Contact",
			properties: [
				{ label: 'Contact Number', value: currentProfile?.contactNumber },
				{ label: 'Email', value: currentProfile?.contactEmail },
			]
		}
	];

	const renderLockedContent = () => (
		<div className="relative">
			<div className="blur-sm">
				<>
					<Section icon={FaUser} title="About">
						<p className="text-gray-700 whitespace-pre-line">{HandleEmptyValue(currentProfile.bio)}</p>
					</Section>
					<Section icon={FaHeart} title="Partner Preferences">
						<p className="text-gray-700 whitespace-pre-line">{HandleEmptyValue(currentProfile.userPreferences)}</p>
					</Section>
				</>
				{profileSections.map((section, index) => (
					<Section key={index} icon={section.icon} title={section.title}>
						{displayProperties(section.properties)}
					</Section>
				))}
			</div>
			<div 
				className="absolute inset-0 flex items-center justify-center cursor-pointer"
				onClick={() => setShowPaymentModal(true)}
			>
				<div className="text-center">
					<FaLock className="text-4xl text-primary mx-auto mb-2" />
					<p className="text-lg font-semibold">Unlock Full Profile</p>
					<p className="text-sm text-gray-600">Subscribe to view complete details</p>
				</div>
			</div>
		</div>
	);

	return (
		<>
			{currentProfile && (
				<div className="container mx-auto mt-12 mb-4">
					<div className="grid grid-cols-1 gap-4">
						<div>	
							<ProfileCard
								profile={currentProfile}
								isNameClickable={false}
								imageSize={300}
							/>
						</div>
						<div className="bg-white shadow rounded-lg p-6 sm:p-8 border border-gray-200">
							{needPayment ? renderLockedContent() : (
								<>
									<Section icon={FaUser} title="About">
										<p className="text-gray-700 whitespace-pre-line">{HandleEmptyValue(currentProfile.bio)}</p>
									</Section>
									<Section icon={FaHeart} title="Partner Preferences">
										<p className="text-gray-700 whitespace-pre-line">{HandleEmptyValue(currentProfile.userPreferences)}</p>
									</Section>
									{profileSections.map((section, index) => (
										<Section key={index} icon={section.icon} title={section.title}>
											{displayProperties(section.properties)}
										</Section>
									))}
								</>
							)}
						</div>
					</div>
				</div>
			)}

			<PaymentModal 
				open={showPaymentModal}
				onClose={() => setShowPaymentModal(false)}
			/>
		</>
	);
};

export default ProfilePage;
